import React, { useEffect, useState } from 'react';
import { ArrowLeft, Cookie, Shield, Settings, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import CookieSettings from '../components/CookieSettings';

const CookiePolicy = () => {
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Cookie Settings Modal */}
      {showSettings && <CookieSettings onClose={() => setShowSettings(false)} />}

      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="flex items-center gap-2 text-gray-600 hover:text-orange-600"
          >
            <ArrowLeft size={20} />
            Voltar ao Início
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center">
              <Cookie className="text-orange-600" size={32} />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Política de Cookies</h1>
              <p className="text-gray-600 mt-1">Última atualização: {new Date().toLocaleDateString('pt-PT')}</p>
            </div>
          </div>

          {/* Content */}
          <div className="prose max-w-none space-y-8">
            {/* Introdução */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. O que são Cookies?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cookies são pequenos ficheiros de texto que são colocados no seu computador ou dispositivo móvel 
                quando visita um website. São amplamente utilizados para fazer os websites funcionar de forma mais 
                eficiente, bem como para fornecer informações aos proprietários do site.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Os cookies podem ser "persistentes" ou "de sessão". Os cookies persistentes permanecem no seu 
                dispositivo após fechar o navegador, enquanto os cookies de sessão são eliminados quando fecha o navegador.
              </p>
            </section>

            {/* Como utilizamos cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Como Utilizamos Cookies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                A Somar Distâncias, Lda. utiliza cookies para:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Garantir o funcionamento adequado do website</li>
                <li>Melhorar a experiência do utilizador</li>
                <li>Analisar como os visitantes utilizam o nosso site</li>
                <li>Lembrar as suas preferências e definições</li>
                <li>Entender quais áreas do site são mais populares</li>
              </ul>
            </section>

            {/* Tipos de cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Tipos de Cookies Utilizados</h2>
              
              {/* Cookies Essenciais */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="text-green-600" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">3.1. Cookies Essenciais (Obrigatórios)</h3>
                    <p className="text-gray-700 mb-3">
                      Estes cookies são necessários para o funcionamento básico do website e não podem ser desativados. 
                      Normalmente são definidos apenas em resposta a ações realizadas por si, como definir as suas 
                      preferências de privacidade, iniciar sessão ou preencher formulários.
                    </p>
                    <div className="bg-white rounded-lg p-4 space-y-2">
                      <h4 className="font-semibold text-sm text-gray-900">Exemplos:</h4>
                      <div className="text-sm space-y-1">
                        <p><strong>somar_cookie_consent:</strong> Armazena as suas preferências de cookies</p>
                        <p><strong>Duração:</strong> 1 ano</p>
                        <p><strong>Finalidade:</strong> Lembrar as suas escolhas de consentimento de cookies</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cookies Funcionais */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Settings className="text-blue-600" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">3.2. Cookies Funcionais (Opcionais)</h3>
                    <p className="text-gray-700 mb-3">
                      Estes cookies permitem que o website forneça funcionalidades melhoradas e personalização. 
                      Podem ser definidos por nós ou por fornecedores terceiros cujos serviços adicionámos às nossas páginas.
                    </p>
                    <div className="bg-white rounded-lg p-4 space-y-2">
                      <h4 className="font-semibold text-sm text-gray-900">Exemplos:</h4>
                      <div className="text-sm space-y-1">
                        <p><strong>user_preferences:</strong> Armazena preferências de interface</p>
                        <p><strong>Duração:</strong> 6 meses</p>
                        <p><strong>Finalidade:</strong> Lembrar configurações personalizadas do utilizador</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cookies de Analytics */}
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="text-purple-600" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">3.3. Cookies de Analytics (Opcionais)</h3>
                    <p className="text-gray-700 mb-3">
                      Estes cookies permitem-nos contar visitas e fontes de tráfego para que possamos medir e 
                      melhorar o desempenho do nosso site. Ajudam-nos a saber quais páginas são mais e menos 
                      populares e a ver como os visitantes se movem pelo site.
                    </p>
                    <div className="bg-white rounded-lg p-4 space-y-3">
                      <h4 className="font-semibold text-sm text-gray-900">Vercel Analytics:</h4>
                      <div className="text-sm space-y-1">
                        <p><strong>Cookies utilizados:</strong> __vercel_* (cookies de analytics)</p>
                        <p><strong>Duração:</strong> Variável (de sessão até 1 ano)</p>
                        <p><strong>Finalidade:</strong> Análise de tráfego, métricas de utilização, performance do site</p>
                        <p><strong>Fornecedor:</strong> Vercel Inc. (USA)</p>
                        <p><strong>Mais informações:</strong>{' '}
                          <a 
                            href="https://vercel.com/docs/analytics/privacy-policy" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-purple-600 hover:underline"
                          >
                            Política de Privacidade Vercel Analytics
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Cookies de terceiros */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Cookies de Terceiros</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Além dos nossos próprios cookies, podemos utilizar vários cookies de terceiros para reportar 
                estatísticas de uso do website e fornecer funcionalidades adicionais:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Google Maps</h4>
                  <p className="text-sm text-gray-700">
                    Utilizamos o Google Maps para mostrar a nossa localização. O Google pode definir cookies 
                    para este serviço.
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Política de Privacidade:{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
                      https://policies.google.com/privacy
                    </a>
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Vercel Analytics</h4>
                  <p className="text-sm text-gray-700">
                    Utilizamos Vercel Analytics para análise de tráfego e métricas de utilização do website.
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Política de Privacidade:{' '}
                    <a href="https://vercel.com/docs/analytics/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
                      https://vercel.com/docs/analytics/privacy-policy
                    </a>
                  </p>
                </div>
              </div>
            </section>

            {/* Gestão de cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Como Gerir os Cookies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Pode gerir as suas preferências de cookies de várias formas:
              </p>
              
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">5.1. Através das Nossas Definições</h3>
                <p className="text-gray-700 mb-4">
                  Pode alterar as suas preferências de cookies a qualquer momento clicando no botão abaixo:
                </p>
                <Button
                  onClick={() => setShowSettings(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <Settings className="mr-2" size={18} />
                  Gerir Preferências de Cookies
                </Button>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">5.2. Através do Seu Navegador</h3>
                <p className="text-gray-700 mb-3">
                  A maioria dos navegadores permite que controle os cookies através das suas definições. 
                  Pode configurar o seu navegador para recusar todos os cookies ou para indicar quando um cookie está a ser enviado.
                </p>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-700"><strong>Google Chrome:</strong>{' '}
                    <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
                      Gerir cookies no Chrome
                    </a>
                  </p>
                  <p className="text-gray-700"><strong>Mozilla Firefox:</strong>{' '}
                    <a href="https://support.mozilla.org/pt-PT/kb/gerir-cookies" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
                      Gerir cookies no Firefox
                    </a>
                  </p>
                  <p className="text-gray-700"><strong>Safari:</strong>{' '}
                    <a href="https://support.apple.com/pt-pt/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
                      Gerir cookies no Safari
                    </a>
                  </p>
                  <p className="text-gray-700"><strong>Microsoft Edge:</strong>{' '}
                    <a href="https://support.microsoft.com/pt-pt/microsoft-edge/eliminar-cookies-no-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
                      Gerir cookies no Edge
                    </a>
                  </p>
                </div>
                <p className="text-sm text-gray-600 mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <strong>⚠️ Aviso:</strong> Se desativar os cookies, algumas funcionalidades do website podem não funcionar corretamente.
                </p>
              </div>
            </section>

            {/* Duração dos cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Duração dos Cookies</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b">
                        Tipo de Cookie
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b">
                        Duração Típica
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Essenciais</td>
                      <td className="px-6 py-4 text-sm text-gray-700">Sessão até 1 ano</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Funcionais</td>
                      <td className="px-6 py-4 text-sm text-gray-700">6 meses a 1 ano</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Analytics</td>
                      <td className="px-6 py-4 text-sm text-gray-700">Até 14 meses</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Alterações */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Alterações a Esta Política</h2>
              <p className="text-gray-700 leading-relaxed">
                Podemos atualizar esta Política de Cookies periodicamente para refletir mudanças nas nossas práticas 
                ou por outras razões operacionais, legais ou regulamentares. Recomendamos que consulte esta página 
                regularmente para se manter informado sobre como utilizamos cookies.
              </p>
            </section>

            {/* Contacto */}
            <section className="bg-orange-50 border border-orange-200 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Contacto</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Se tiver questões sobre a nossa utilização de cookies, pode contactar-nos:
              </p>
              <div className="space-y-2 text-sm">
                <p className="text-gray-700">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:somardistancias8.geral@gmail.com" className="text-orange-600 hover:underline">
                    somardistancias8.geral@gmail.com
                  </a>
                </p>
                <p className="text-gray-700">
                  <strong>Telefone:</strong>{' '}
                  <a href="tel:+351961313663" className="text-orange-600 hover:underline">
                    +351 961 313 663
                  </a>
                </p>
              </div>
            </section>
          </div>

          {/* Back Button */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <Button
              onClick={() => navigate('/')}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <ArrowLeft size={20} className="mr-2" />
              Voltar ao Início
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
